import { supabase } from "@/api/supabaseClient";
import type { ClubReactionKey } from "@/api/club/reactionKeys";

function newId(): string {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export type ClubItemType = "post" | "poll" | "event";
export type ClubItemStatus = "active" | "withdrawn" | "removed";
export type EventRsvpStatus = "yes" | "maybe" | "no";

export type ClubReactionSummary = {
  reaction_key: ClubReactionKey | string;
  count: number;
};

export type ClubFeedItem = {
  item_id: string;
  organization_id: string;
  item_type: ClubItemType;
  status: ClubItemStatus;
  created_by: string;
  author_display_name?: string | null;
  author_roles?: string[];
  created_at: string;
  updated_at?: string;
  title?: string | null;
  body?: string | null;
  description?: string | null;
  is_official?: boolean;
  pinned_at?: string | null;
  allow_multiple?: boolean;
  ends_at?: string | null;
  closed_at?: string | null;
  is_closed?: boolean;
  my_voted?: boolean;
  starts_at?: string | null;
  ends_at_event?: string | null;
  all_day?: boolean;
  location_text?: string | null;
  online_url?: string | null;
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  max_attendees?: number | null;
  rsvp_deadline?: string | null;
  my_rsvp?: EventRsvpStatus | null;
  yes_count?: number;
  comment_count?: number;
  reaction_counts?: ClubReactionSummary[];
  my_reaction?: string | null;
  watching?: boolean;
  watch_notify?: boolean;
  poll_options?: ClubPollOption[];
  [key: string]: unknown;
};

export type ClubPollOption = {
  id: string;
  label: string;
  sort_order?: number;
  vote_count?: number;
  my_vote?: boolean;
};

export type ClubComment = {
  id: string;
  organization_id: string;
  item_id: string;
  author_id: string;
  author_display_name?: string | null;
  author_roles?: string[];
  body: string | null;
  edited_at: string | null;
  withdrawn_at: string | null;
  withdrawn_by: string | null;
  client_comment_id: string | null;
  created_at: string;
  my_reaction?: string | null;
  reaction_counts?: ClubReactionSummary[];
  mentions?: Array<{ profile_id: string; display_name?: string | null }>;
};

export type ClubEventRow = {
  item_id: string;
  organization_id: string;
  title: string;
  description?: string | null;
  starts_at: string;
  ends_at?: string | null;
  all_day?: boolean;
  location_text?: string | null;
  online_url?: string | null;
  max_attendees?: number | null;
  rsvp_deadline?: string | null;
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  series_id?: string | null;
  my_rsvp?: EventRsvpStatus | null;
  yes_count?: number;
  maybe_count?: number;
  no_count?: number;
  watching?: boolean;
  [key: string]: unknown;
};

export type EventHelperRole = {
  id: string;
  event_item_id: string;
  name: string;
  desired_count: number | null;
  sort_order: number;
  assignments?: Array<{
    profile_id: string;
    display_name?: string | null;
    assigned_by?: string | null;
  }>;
  my_assigned?: boolean;
};

export type CaseChip = {
  case_id: string;
  case_number: string | null;
  name: string | null;
  status: string | null;
  available?: boolean;
};

export type MemberMiniProfile = {
  profile_id: string;
  display_name: string | null;
  roles: string[];
  membership_status: "active" | "disabled" | "invited" | string;
};

export type EventSeriesInput = {
  freq: "weekly" | "monthly_dom" | "monthly_weekday";
  interval_n?: number;
  until?: string | null;
  weekday?: number | null;
  day_of_month?: number | null;
  weekday_position?: number | null;
};

export class ClubApi {
  static newClientItemId(): string {
    return newId();
  }

  static newClientCommentId(): string {
    return newId();
  }

  // ── Posts / Polls / Events ──────────────────────────────────────────

  static async createClubPost(input: {
    organizationId: string;
    clientItemId?: string;
    title?: string | null;
    body: string;
    isOfficial?: boolean;
    mentionedProfileIds?: string[];
  }) {
    const { data, error } = await supabase.rpc("create_club_post", {
      p_organization_id: input.organizationId,
      p_client_item_id: input.clientItemId ?? newId(),
      p_title: input.title ?? null,
      p_body: input.body,
      p_is_official: input.isOfficial ?? false,
      p_mentioned_profile_ids: input.mentionedProfileIds ?? [],
    });
    if (error) throw error;
    return data;
  }

  static async editClubPost(input: {
    itemId: string;
    title?: string | null;
    body: string;
    mentionedProfileIds?: string[];
  }) {
    const { data, error } = await supabase.rpc("edit_club_post", {
      p_item_id: input.itemId,
      p_title: input.title ?? null,
      p_body: input.body,
      p_mentioned_profile_ids: input.mentionedProfileIds ?? [],
    });
    if (error) throw error;
    return data;
  }

  static async setClubPostOfficial(itemId: string, isOfficial: boolean) {
    const { data, error } = await supabase.rpc("set_club_post_official", {
      p_item_id: itemId,
      p_is_official: isOfficial,
    });
    if (error) throw error;
    return data;
  }

  static async setClubPostPinned(itemId: string, pinned: boolean) {
    const { data, error } = await supabase.rpc("set_club_post_pinned", {
      p_item_id: itemId,
      p_pinned: pinned,
    });
    if (error) throw error;
    return data;
  }

  static async createClubPoll(input: {
    organizationId: string;
    clientItemId?: string;
    title: string;
    body?: string | null;
    allowMultiple: boolean;
    endsAt?: string | null;
    options: string[];
    mentionedProfileIds?: string[];
  }) {
    const { data, error } = await supabase.rpc("create_club_poll", {
      p_organization_id: input.organizationId,
      p_client_item_id: input.clientItemId ?? newId(),
      p_title: input.title,
      p_body: input.body ?? null,
      p_allow_multiple: input.allowMultiple,
      p_ends_at: input.endsAt ?? null,
      p_options: input.options,
      p_mentioned_profile_ids: input.mentionedProfileIds ?? [],
    });
    if (error) throw error;
    return data;
  }

  static async castPollVote(pollItemId: string, optionIds: string[]) {
    const { data, error } = await supabase.rpc("cast_poll_vote", {
      p_poll_item_id: pollItemId,
      p_option_ids: optionIds,
    });
    if (error) throw error;
    return data;
  }

  static async closeClubPoll(itemId: string) {
    const { data, error } = await supabase.rpc("close_club_poll", {
      p_item_id: itemId,
    });
    if (error) throw error;
    return data;
  }

  static async editClubPollMeta(input: {
    itemId: string;
    title: string;
    body?: string | null;
  }) {
    const { data, error } = await supabase.rpc("edit_club_poll_meta", {
      p_item_id: input.itemId,
      p_title: input.title,
      p_body: input.body ?? null,
    });
    if (error) throw error;
    return data;
  }

  static async createClubEvent(input: {
    organizationId: string;
    clientItemId?: string;
    title: string;
    description?: string | null;
    startsAt: string;
    endsAt?: string | null;
    allDay?: boolean;
    locationText?: string | null;
    onlineUrl?: string | null;
    maxAttendees?: number | null;
    rsvpDeadline?: string | null;
    series?: EventSeriesInput | null;
    mentionedProfileIds?: string[];
  }) {
    const { data, error } = await supabase.rpc("create_club_event", {
      p_organization_id: input.organizationId,
      p_client_item_id: input.clientItemId ?? newId(),
      p_title: input.title,
      p_description: input.description ?? null,
      p_starts_at: input.startsAt,
      p_ends_at: input.endsAt ?? null,
      p_all_day: input.allDay ?? false,
      p_location_text: input.locationText ?? null,
      p_online_url: input.onlineUrl ?? null,
      p_max_attendees: input.maxAttendees ?? null,
      p_rsvp_deadline: input.rsvpDeadline ?? null,
      p_series: input.series ?? null,
      p_mentioned_profile_ids: input.mentionedProfileIds ?? [],
    });
    if (error) throw error;
    return data;
  }

  static async editClubEvent(input: {
    itemId: string;
    title: string;
    description?: string | null;
    startsAt: string;
    endsAt?: string | null;
    allDay?: boolean;
    locationText?: string | null;
    onlineUrl?: string | null;
    maxAttendees?: number | null;
    rsvpDeadline?: string | null;
    mentionedProfileIds?: string[];
  }) {
    const { data, error } = await supabase.rpc("edit_club_event", {
      p_item_id: input.itemId,
      p_title: input.title,
      p_description: input.description ?? null,
      p_starts_at: input.startsAt,
      p_ends_at: input.endsAt ?? null,
      p_all_day: input.allDay ?? false,
      p_location_text: input.locationText ?? null,
      p_online_url: input.onlineUrl ?? null,
      p_max_attendees: input.maxAttendees ?? null,
      p_rsvp_deadline: input.rsvpDeadline ?? null,
      p_mentioned_profile_ids: input.mentionedProfileIds ?? [],
    });
    if (error) throw error;
    return data;
  }

  static async cancelClubEvent(itemId: string, reason: string) {
    const { data, error } = await supabase.rpc("cancel_club_event", {
      p_item_id: itemId,
      p_reason: reason,
    });
    if (error) throw error;
    return data;
  }

  static async extendEventSeries(seriesId: string, until: string) {
    const { data, error } = await supabase.rpc("extend_event_series", {
      p_series_id: seriesId,
      p_until: until,
    });
    if (error) throw error;
    return data;
  }

  static async cancelEventSeriesRemainder(seriesId: string, reason: string) {
    const { data, error } = await supabase.rpc("cancel_event_series_remainder", {
      p_series_id: seriesId,
      p_reason: reason,
    });
    if (error) throw error;
    return data;
  }

  static async adminRemoveClubItem(itemId: string, reason: string) {
    const { data, error } = await supabase.rpc("admin_remove_club_item", {
      p_item_id: itemId,
      p_reason: reason,
    });
    if (error) throw error;
    return data;
  }

  static async withdrawClubItem(itemId: string) {
    const { data, error } = await supabase.rpc("withdraw_club_item", {
      p_item_id: itemId,
    });
    if (error) throw error;
    return data;
  }

  // ── Social ──────────────────────────────────────────────────────────

  static async addClubComment(input: {
    itemId: string;
    clientCommentId?: string;
    body: string;
    mentionedProfileIds?: string[];
  }): Promise<ClubComment> {
    const { data, error } = await supabase.rpc("add_club_comment", {
      p_item_id: input.itemId,
      p_client_comment_id: input.clientCommentId ?? newId(),
      p_body: input.body,
      p_mentioned_profile_ids: input.mentionedProfileIds ?? [],
    });
    if (error) throw error;
    return data as ClubComment;
  }

  static async editClubComment(input: {
    commentId: string;
    body: string;
    mentionedProfileIds?: string[];
  }): Promise<ClubComment> {
    const { data, error } = await supabase.rpc("edit_club_comment", {
      p_comment_id: input.commentId,
      p_body: input.body,
      p_mentioned_profile_ids: input.mentionedProfileIds ?? [],
    });
    if (error) throw error;
    return data as ClubComment;
  }

  static async withdrawClubComment(commentId: string) {
    const { data, error } = await supabase.rpc("withdraw_club_comment", {
      p_comment_id: commentId,
    });
    if (error) throw error;
    return data;
  }

  static async listClubComments(itemId: string): Promise<ClubComment[]> {
    const { data, error } = await supabase.rpc("list_club_comments", {
      p_item_id: itemId,
    });
    if (error) throw error;
    return (data ?? []) as ClubComment[];
  }

  static async setClubReaction(input: {
    itemId?: string | null;
    commentId?: string | null;
    reactionKey?: string | null;
  }) {
    const { data, error } = await supabase.rpc("set_club_reaction", {
      p_item_id: input.itemId ?? null,
      p_comment_id: input.commentId ?? null,
      p_reaction_key: input.reactionKey ?? null,
    });
    if (error) throw error;
    return data;
  }

  static async setClubWatch(itemId: string, watching: boolean) {
    const { data, error } = await supabase.rpc("set_club_watch", {
      p_item_id: itemId,
      p_watching: watching,
    });
    if (error) throw error;
    return data;
  }

  static async setClubWatchNotify(itemId: string, enabled: boolean) {
    const { data, error } = await supabase.rpc("set_club_watch_notify", {
      p_item_id: itemId,
      p_enabled: enabled,
    });
    if (error) throw error;
    return data;
  }

  // ── RSVP / Helpers ──────────────────────────────────────────────────

  static async setEventRsvp(eventItemId: string, status: EventRsvpStatus) {
    const { data, error } = await supabase.rpc("set_event_rsvp", {
      p_event_item_id: eventItemId,
      p_status: status,
    });
    if (error) throw error;
    return data;
  }

  static async upsertEventHelperRole(input: {
    eventItemId: string;
    roleId?: string | null;
    name: string;
    desiredCount?: number | null;
    sortOrder?: number;
  }) {
    const { data, error } = await supabase.rpc("upsert_event_helper_role", {
      p_event_item_id: input.eventItemId,
      p_role_id: input.roleId ?? null,
      p_name: input.name,
      p_desired_count: input.desiredCount ?? null,
      p_sort_order: input.sortOrder ?? 0,
    });
    if (error) throw error;
    return data;
  }

  static async deleteEventHelperRole(roleId: string) {
    const { data, error } = await supabase.rpc("delete_event_helper_role", {
      p_role_id: roleId,
    });
    if (error) throw error;
    return data;
  }

  static async joinHelperRole(roleId: string) {
    const { data, error } = await supabase.rpc("join_helper_role", {
      p_role_id: roleId,
    });
    if (error) throw error;
    return data;
  }

  static async leaveHelperRole(roleId: string) {
    const { data, error } = await supabase.rpc("leave_helper_role", {
      p_role_id: roleId,
    });
    if (error) throw error;
    return data;
  }

  static async assignHelper(roleId: string, profileId: string) {
    const { data, error } = await supabase.rpc("assign_helper", {
      p_role_id: roleId,
      p_profile_id: profileId,
    });
    if (error) throw error;
    return data;
  }

  static async unassignHelper(roleId: string, profileId: string) {
    const { data, error } = await supabase.rpc("unassign_helper", {
      p_role_id: roleId,
      p_profile_id: profileId,
    });
    if (error) throw error;
    return data;
  }

  // ── Read / Search ───────────────────────────────────────────────────

  static async listClubFeed(input: {
    organizationId: string;
    cursor?: string | null;
    limit?: number;
  }): Promise<ClubFeedItem[]> {
    const { data, error } = await supabase.rpc("list_club_feed", {
      p_organization_id: input.organizationId,
      p_cursor: input.cursor ?? null,
      p_limit: input.limit ?? 30,
    });
    if (error) throw error;
    return (data ?? []) as ClubFeedItem[];
  }

  static async getClubItem(itemId: string): Promise<ClubFeedItem | null> {
    const { data, error } = await supabase.rpc("get_club_item", {
      p_item_id: itemId,
    });
    if (error) throw error;
    if (!data) return null;
    return data as ClubFeedItem;
  }

  static async listEventsBetween(input: {
    organizationId: string;
    from: string;
    to: string;
  }): Promise<ClubEventRow[]> {
    const { data, error } = await supabase.rpc("list_events_between", {
      p_organization_id: input.organizationId,
      p_from: input.from,
      p_to: input.to,
    });
    if (error) throw error;
    return (data ?? []) as ClubEventRow[];
  }

  static async searchClub(input: {
    organizationId: string;
    query: string;
    types?: ClubItemType[] | null;
    limit?: number;
  }): Promise<ClubFeedItem[]> {
    const { data, error } = await supabase.rpc("search_club", {
      p_organization_id: input.organizationId,
      p_query: input.query,
      p_types: input.types ?? null,
      p_limit: input.limit ?? 30,
    });
    if (error) throw error;
    return (data ?? []) as ClubFeedItem[];
  }

  static async resolveCaseChips(caseIds: string[]): Promise<CaseChip[]> {
    if (caseIds.length === 0) return [];
    const { data, error } = await supabase.rpc("resolve_case_chips", {
      p_case_ids: caseIds,
    });
    if (error) throw error;
    return (data ?? []).map((row: any) => ({
      case_id: row.case_id ?? row.id,
      case_number: row.case_number ?? null,
      name: row.name ?? null,
      status: row.status ?? null,
      available: row.available !== false && !!(row.case_number || row.name),
    })) as CaseChip[];
  }

  static async getMemberMiniProfile(
    profileId: string
  ): Promise<MemberMiniProfile | null> {
    const { data, error } = await supabase.rpc("get_member_mini_profile", {
      p_profile_id: profileId,
    });
    if (error) throw error;
    if (!data) return null;
    const row = data as any;
    return {
      profile_id: row.profile_id ?? profileId,
      display_name: row.display_name ?? null,
      roles: row.roles ?? [],
      membership_status: row.membership_status ?? row.status ?? "active",
    };
  }
}
